import type { Editor } from '@tiptap/core';

import type { FlowFontCategory } from './fontStack';

/** The sizes Word's own dropdown offers, which is what an analyst expects to find here. */
export const FONT_SIZES_PT = [8, 9, 10, 11, 12, 14, 16, 18, 20, 24] as const;

/** One indent step. The same 8mm the renderer indents a list level by. */
export const INDENT_STEP_MM = 8;

/**
 * The faces on offer, named as Word names them.
 *
 * The model stores a CATEGORY, because the renderer only has three embedded faces — so the
 * dropdown offers the typeface each category stands for rather than a list the PDF could not
 * honour. Picking "Times New Roman" here is picking `serif`.
 */
export const FONT_FACES: readonly { label: string; category: FlowFontCategory }[] = [
	{ label: 'Arial', category: 'sans-serif' },
	{ label: 'Times New Roman', category: 'serif' },
	{ label: 'Courier New', category: 'monospace' },
];

const PARAGRAPH = 'paragraph';

/**
 * The toolbar's commands, as plain functions over an editor.
 *
 * Separated from the toolbar's markup so each one is testable without rendering a button, and
 * because the interesting ones are not TipTap built-ins: alignment, size and indent are
 * ATTRIBUTES of the flow model's paragraph, not marks, so they go through `updateAttributes`
 * and land in the document the renderer reads rather than in editor-only state.
 */
export const setParagraphAlign = (editor: Editor, align: 'left' | 'center' | 'right'): void => {
	// Left is the model's absence-of-alignment, so it is cleared rather than stored.
	editor
		.chain()
		.focus()
		.updateAttributes(PARAGRAPH, { align: align === 'left' ? null : align })
		.run();
};

export const setParagraphFontFamily = (editor: Editor, category: FlowFontCategory): void => {
	editor.chain().focus().updateAttributes(PARAGRAPH, { fontFamily: category }).run();
};

export const setParagraphFontSize = (editor: Editor, fontSizePt: number): void => {
	editor.chain().focus().updateAttributes(PARAGRAPH, { fontSizePt }).run();
};

/**
 * Steps the left indent, floored at zero.
 *
 * Read from the current paragraph rather than tracked, so pressing indent twice on a paragraph
 * that arrived from Word at 12.7mm gives 20.7mm — it moves the document's own value instead of
 * replacing it with a number the editor invented.
 */
export const stepParagraphIndent = (editor: Editor, stepMm: number): void => {
	const current: unknown = editor.getAttributes(PARAGRAPH).indentLeftMm;
	const from = typeof current === 'number' ? current : 0;
	const next = Math.max(0, from + stepMm);
	editor
		.chain()
		.focus()
		.updateAttributes(PARAGRAPH, { indentLeftMm: next === 0 ? null : next })
		.run();
};

/** The size shown in the dropdown: the paragraph's own, or the document's base. */
export const activeFontSizePt = (editor: Editor, baseFontSizePt: number): number => {
	const current: unknown = editor.getAttributes(PARAGRAPH).fontSizePt;
	return typeof current === 'number' ? current : baseFontSizePt;
};

/** The face shown in the dropdown: the paragraph's own, or the document's base. */
export const activeFontFamily = (
	editor: Editor,
	baseFontFamily: FlowFontCategory
): FlowFontCategory => {
	const current: unknown = editor.getAttributes(PARAGRAPH).fontFamily;
	// Found rather than asserted: the returned category comes off FONT_FACES, so it is one of
	// the three the renderer has a face for whatever the attribute happens to hold.
	const face = FONT_FACES.find((candidate) => candidate.category === current);
	return face?.category ?? baseFontFamily;
};

export const activeAlign = (editor: Editor): 'left' | 'center' | 'right' => {
	const current: unknown = editor.getAttributes(PARAGRAPH).align;
	if (current === 'center') {
		return 'center';
	}
	return current === 'right' ? 'right' : 'left';
};
